import React from 'react';
import {
  Controller,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import {
  HtmlTable,
  HtmlTableProps,
} from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Input } from '../../../../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { BeneficiaryType } from '../../../../../../../../ProfileOPL/PersonalInfo/api/useTrustsQuery';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Beneficiaries as BaseBeneficiaries, FormFields } from '../../AccountForm.meta';
import { DisabledRow } from './components/DisabledRow/DisabledRow';

interface Beneficiaries extends BaseBeneficiaries {
  fieldKey: string;
}

interface Props extends HtmlTableProps {
  type: BeneficiaryType;
  options: { label: string; value: string | null; isVisible: boolean }[];
  onUpdateOptions: React.Dispatch<
    React.SetStateAction<
      {
        label: string;
        value: string | null;
        isVisible: boolean;
      }[]
    >
  >;
  beneficiaryFields: Beneficiaries[];
  onAppend: UseFieldArrayAppend<FormFields, 'bankAccount.AccountBeneficiaries.Beneficiaries'>;
  onRemove: UseFieldArrayRemove;
  isReadOnly: boolean;
}

export const BeneficiariesTable = ({
  type,
  options,
  onUpdateOptions,
  beneficiaryFields,
  onAppend,
  onRemove,
  isReadOnly,
  ...rest
}: Props) => {
  const { control, watch, register, setValue } = useFormContext<FormFields>();

  const watchAccountBeneficiaries = watch('bankAccount.AccountBeneficiaries');

  const onlyVisibleOptions = options.filter(({ isVisible }) => isVisible);

  const total =
    watchAccountBeneficiaries?.Beneficiaries.filter(b => b.Type === type).reduce(
      (acc, b) => +(b?.Percentage || 0) + acc,
      0,
    ) || 0;

  return (
    <HtmlTable variant="embedded" columnSpacing="small" fixedHead {...rest}>
      <table>
        <colgroup>
          <col width="60%" />
          <col width="30%" />
          <col width="10%" />
        </colgroup>

        <thead>
          <tr>
            <th>
              <Text variant="smallCaps" textAlign="left">
                {type === BeneficiaryType.Primary ? 'Primary' : 'Contingent'}
              </Text>
            </th>
            <th colSpan={2}>
              <Text variant="smallCaps" textAlign="left">
                Allocation
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {beneficiaryFields.filter(({ Type }) => Type === type).length === 0 && isReadOnly && (
            <DisabledRow />
          )}

          {beneficiaryFields.map((beneficiary, index) => {
            if (beneficiary.Type !== type) {
              return null;
            }

            return (
              <tr key={beneficiary.fieldKey}>
                <td>
                  <Controller
                    control={control}
                    name={`bankAccount.AccountBeneficiaries.Beneficiaries.${index}.Id`}
                    render={({ field: { value, onChange, ref } }) => (
                      <Select
                        aria-label="Beneficiary"
                        isCreatable
                        disabled={isReadOnly}
                        innerRef={ref}
                        value={options.find(b =>
                          b.value !== null ? b.value === value : b.label === beneficiary.Name,
                        )}
                        options={onlyVisibleOptions}
                        onCreateOption={name => {
                          onChange(null);

                          setValue(
                            `bankAccount.AccountBeneficiaries.Beneficiaries.${index}.Name`,
                            name,
                          );

                          onUpdateOptions(state => {
                            return [...state, { label: name, value: null, isVisible: false }];
                          });
                        }}
                        onChange={opt => {
                          if (opt?.value) {
                            onChange(opt.value);

                            setValue(
                              `bankAccount.AccountBeneficiaries.Beneficiaries.${index}.Name`,
                              opt.label,
                            );

                            onUpdateOptions(state => {
                              return state.map(beneficiaryOption => {
                                if (opt?.value === beneficiaryOption.value) {
                                  return {
                                    ...beneficiaryOption,
                                    isVisible: false,
                                  };
                                }

                                return beneficiaryOption;
                              });
                            });
                          }
                        }}
                        isPortal
                      />
                    )}
                  />
                </td>
                <td>
                  <Input
                    {...register(
                      `bankAccount.AccountBeneficiaries.Beneficiaries.${index}.Percentage`,
                    )}
                    aria-label="Beneficiary allocation percentage"
                    disabled={isReadOnly}
                    type="number"
                    placeholder="0"
                    rightIcon="far fa-percent"
                  />
                </td>
                <td>
                  {!isReadOnly && (
                    <IconButton
                      aria-label="Remove beneficiary"
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => {
                        onUpdateOptions(state => {
                          return state.map(beneficiaryOption => {
                            if (
                              beneficiaryOption.value ===
                              watchAccountBeneficiaries?.Beneficiaries[index].Id
                            ) {
                              return {
                                ...beneficiaryOption,
                                isVisible: true,
                              };
                            }

                            return beneficiaryOption;
                          });
                        });

                        onRemove(index);
                      }}
                    />
                  )}
                </td>
              </tr>
            );
          })}

          {!isReadOnly && (
            <tr>
              <td colSpan={3}>
                <Button
                  variant="link"
                  leftIcon="fas fa-plus"
                  onClick={() => onAppend({ Type: type, Name: '', Percentage: '', Id: null })}
                >
                  Add a Beneficiary
                </Button>
              </td>
            </tr>
          )}
          <tr>
            <td>
              <Text fontWeight={600} textAlign="right">
                Total Allocation
              </Text>
            </td>
            <td colSpan={2}>
              <Text fontWeight={600}>{total}%</Text>
            </td>
          </tr>
        </tbody>
      </table>
    </HtmlTable>
  );
};
